import { Component, Input } from '@angular/core';
import { GlossaryService } from '../../services/glossary.service';

/**
 * Mini-barra SVG inline que muestra el rango P10-P50-P90 de una estimación.
 * No depende de ninguna librería externa. Render solo si p10 y p90 existen.
 *
 * Geometría:
 *  - Eje horizontal: posición proporcional dentro de [p10, p90].
 *  - Marker P50 destacado en el medio.
 *  - Si p50 sale del rango [p10, p90] (caso patológico), se clampa.
 */
@Component({
  selector: 'app-task-range-bar',
  templateUrl: './task-range-bar.component.html',
  styleUrls: ['./task-range-bar.component.scss']
})
export class TaskRangeBarComponent {
  @Input() p10?: number;
  @Input() p50?: number;
  @Input() p90?: number;

  /** Anchura del SVG en píxeles. La altura es fija (10px). */
  @Input() width: number = 80;

  readonly height: number = 10;
  readonly padding: number = 1;

  constructor(private glossary: GlossaryService) {}

  get hasRange(): boolean {
    return typeof this.p10 === 'number'
        && typeof this.p90 === 'number'
        && this.p90! > this.p10!;
  }

  /** Posición X (en píxeles) del marker P50 dentro de la barra. */
  get markerX(): number {
    if (!this.hasRange || typeof this.p50 !== 'number') return this.width / 2;
    const usable = this.width - this.padding * 2;
    const range = this.p90! - this.p10!;
    const clampedP50 = Math.max(this.p10!, Math.min(this.p90!, this.p50));
    const ratio = (clampedP50 - this.p10!) / range;
    return this.padding + ratio * usable;
  }

  /**
   * Tooltip del rango. Combina el texto didáctico del glosario con
   * los valores concretos P10/P50/P90 de esta tarea.
   */
  get rangeTooltip(): string {
    if (!this.hasRange) return '';
    const explainer = this.glossary.getText('range_bar');
    const parts = [`P10: ${this.p10}h`];
    if (typeof this.p50 === 'number') parts.push(`P50: ${this.p50}h`);
    parts.push(`P90: ${this.p90}h`);
    return `${explainer} · ${parts.join(' · ')}`;
  }
}
